import { defineStore } from 'pinia'
import {
  getChangelogEntries, getChangelogUnread, markChangelogViewed,
  setChangelogReaction, removeChangelogReaction
} from '@/api/changelog'

// axios camelizes response KEYS but not VALUES: `kind` stays 'thumbs_up'
// while the admin-only `entry.reactions` counts arrive keyed 'thumbsUp'.
export const countKey = (kind) => kind.replace(/_(\w)/g, (_, char) => char.toUpperCase())

// Optimistic myReaction + counts shift; counts exist only for admins.
function applyReactionLocally(entry, from, to) {
  entry.myReaction = to
  if (!entry.reactions) return
  if (from) entry.reactions[countKey(from)] = Math.max(0, (entry.reactions[countKey(from)] ?? 0) - 1)
  if (to) entry.reactions[countKey(to)] = (entry.reactions[countKey(to)] ?? 0) + 1
}

/**
 * Drives the Changelog slideout (one panel instance mounted in AppLayout) and
 * the unread dots on its triggers (navbar bullhorn, user menu, MLAP sub-nav).
 * The server renders the union of feeds the viewer can access — every
 * trigger opens the same panel.
 *
 * Reopens race-protect via `activeRequestId`: every open() supersedes
 * in-flight loads, whose responses are then ignored.
 */
export const useChangelogStore = defineStore('changelog', {
  state: () => ({
    isOpen: false,
    entries: [],
    page: 0,
    hasMore: false,
    loading: false,
    loadFailed: false,
    unread: false,
    activeRequestId: 0
  }),

  actions: {
    async open() {
      this.activeRequestId += 1
      this.isOpen = true
      this.entries = []
      this.page = 0
      this.hasMore = false
      this.loading = false // a superseded in-flight load no longer owns this flag
      this.loadFailed = false
      this.markViewed()
      await this.loadMore()
    },

    close() {
      this.isOpen = false
    },

    async loadMore() {
      if (this.loading) return
      const requestId = this.activeRequestId
      this.loading = true
      this.loadFailed = false
      try {
        const { data } = await getChangelogEntries(this.page + 1)
        if (requestId !== this.activeRequestId) return // superseded by a newer open()
        this.entries.push(...data.entries)
        this.page += 1
        this.hasMore = data.meta.hasMore
      } catch {
        if (requestId === this.activeRequestId) this.loadFailed = true
      } finally {
        if (requestId === this.activeRequestId) this.loading = false
      }
    },

    // Thumbs up/down on one entry: the same kind again removes, the other
    // switches. Optimistic, reconciled from the server response, reverted with
    // an inline per-entry error on failure (no toasts). open() replaces every
    // entry object, so post-await writes re-resolve the LIVE entry by id —
    // otherwise they would land on a detached object nothing renders.
    async toggleReaction(entry, kind) {
      if (entry.reactionPending) return
      const requestId = this.activeRequestId
      const liveEntry = () => (requestId === this.activeRequestId ? entry : this.entries.find((e) => e.id === entry.id))
      const previous = entry.myReaction || null
      const next = previous === kind ? null : kind
      entry.reactionPending = true
      entry.reactionFailed = false
      applyReactionLocally(entry, previous, next)
      try {
        const { data } = next ?
          await setChangelogReaction(entry.id, next) :
          await removeChangelogReaction(entry.id)
        const target = liveEntry()
        if (target) {
          target.myReaction = data.myReaction
          if (data.reactions) target.reactions = data.reactions
        }
      } catch {
        // Superseded: the reloaded entry already shows server truth, so only
        // the inline error carries over — no revert math on it.
        if (requestId === this.activeRequestId) applyReactionLocally(entry, next, previous)
        const target = liveEntry()
        if (target) target.reactionFailed = true
      } finally {
        entry.reactionPending = false
      }
    },

    // Both dot updates are best-effort — a failed request must never break the page.
    async fetchUnread() {
      try {
        const { data } = await getChangelogUnread()
        this.unread = data.unread
      } catch { /* keep the dot hidden */ }
    },

    async markViewed() {
      this.unread = false
      try {
        await markChangelogViewed()
      } catch { /* re-shows on next page load at worst */ }
    }
  }
})
